/**
 * Voice Assistant Web Component
 *
 * Real-time voice interaction with Claude AI via WebSocket.
 * Features audio recording, transcription, AI responses, and token tracking.
 * @element voice-assistant
 * @attr {string} ws-url - WebSocket URL (default: "ws://localhost:8000/ws/assistant")
 * @fires recording-start - Fired when recording starts
 * @fires recording-stop - Fired when recording stops
 * @fires transcription - Fired when transcription received
 * @fires response - Fired when AI response received
 * @fires error - Fired on errors
 */
export class VoiceAssistant extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });

    // State
    this._state = 'idle'; // idle, recording, processing
    this._ws = null;
    this._mediaRecorder = null;
    this._audioChunks = [];
    this._pingInterval = null;

    // Token tracking
    this.totalTokensUsed = 0;
    this.sessionTokens = [];
  }

  connectedCallback() {
    this.render();
    this._attachEventListeners();
  }

  disconnectedCallback() {
    this._cleanup();
  }

  render() {
    this.shadowRoot.innerHTML = `
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        :host {
          display: block;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .container {
          width: 100%;
          max-width: 600px;
          margin: 0 auto;
          background: white;
          border-radius: 20px;
          padding: 40px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.3);
          display: flex;
          flex-direction: column;
        }

        h1 {
          text-align: center;
          color: #333;
          margin-bottom: 10px;
          font-size: 2em;
        }

        .subtitle {
          text-align: center;
          color: #666;
          font-size: 1em;
          margin-bottom: 20px;
        }

        .status {
          text-align: center;
          padding: 15px;
          border-radius: 10px;
          margin-bottom: 20px;
          font-weight: 500;
        }

        .status.connected {
          background: #d4edda;
          color: #155724;
        }

        .status.disconnected {
          background: #f8d7da;
          color: #721c24;
        }

        .status.recording {
          background: #fff3cd;
          color: #856404;
          animation: pulse 1s infinite;
        }

        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.7; }
        }

        .messages {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          background: #f8f9fa;
          border-radius: 10px;
          margin-bottom: 20px;
          min-height: 300px;
          max-height: 500px;
        }

        .message {
          margin-bottom: 15px;
          padding: 10px 15px;
          border-radius: 8px;
          line-height: 1.5;
          position: relative;
          animation: slideIn 0.2s ease-out;
        }

        @keyframes slideIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .message.user {
          background: #4a7c2c;
          color: white;
          margin-left: 20%;
        }

        .message.assistant {
          background: #e9ecef;
          color: #333;
          margin-right: 20%;
        }

        .message.system {
          background: #fff3cd;
          color: #856404;
          text-align: center;
          font-size: 0.9em;
        }

        .timestamp {
          font-size: 0.75em;
          opacity: 0.6;
          margin-top: 5px;
          font-family: monospace;
        }

        .message.user .timestamp {
          color: rgba(255, 255, 255, 0.8);
        }

        .message.assistant .timestamp,
        .message.system .timestamp {
          color: rgba(0, 0, 0, 0.5);
        }

        .controls {
          text-align: center;
        }

        button {
          background: linear-gradient(135deg, #4a7c2c 0%, #2d5016 100%);
          color: white;
          border: none;
          padding: 20px 60px;
          font-size: 1.2em;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s;
          font-weight: 600;
          box-shadow: 0 4px 15px rgba(0,0,0,0.2);
        }

        button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(0,0,0,0.3);
        }

        button:active {
          transform: translateY(0);
        }

        button.recording {
          background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
          animation: pulse 1s infinite;
        }

        button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .hint {
          text-align: center;
          color: #6c757d;
          margin-top: 15px;
          font-size: 0.9em;
        }

        @media (max-width: 768px) {
          .container {
            padding: 20px;
          }

          h1 {
            font-size: 1.5em;
          }

          button {
            padding: 15px 40px;
            font-size: 1em;
          }
        }
      </style>

      <div class="container">
        <h1>🏕️ Campground Manager AI</h1>
        <div class="subtitle">Your intelligent booking assistant</div>
        <div class="status connected" data-display="status">Connected</div>

        <div class="messages" data-display="messages">
          <div class="message system">Hold the button and speak!</div>
        </div>

        <div class="controls">
          <button data-action="record">Hold to Talk</button>
          <div class="hint">Press and hold to record, release to send</div>
        </div>
      </div>
    `;
  }

  _attachEventListeners() {
    const recordButton = this.shadowRoot.querySelector('[data-action="record"]');

    // Hold to talk - mousedown/mouseup
    recordButton.addEventListener('mousedown', () => this._startRecording());
    recordButton.addEventListener('mouseup', () => this._stopRecording());
    recordButton.addEventListener('mouseleave', () => {
      if (this._state === 'recording') {
        this._stopRecording();
      }
    });

    // Touch support
    recordButton.addEventListener('touchstart', (e) => {
      e.preventDefault();
      this._startRecording();
    });
    recordButton.addEventListener('touchend', (e) => {
      e.preventDefault();
      this._stopRecording();
    });
  }

  async _startRecording() {
    if (this._state !== 'idle') return;

    try {
      this._setState('recording');
      this._connectWebSocket();

      // Request microphone access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Start recording
      this._mediaRecorder = new MediaRecorder(stream);
      this._audioChunks = [];

      this._mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          this._audioChunks.push(event.data);
        }
      };

      this._mediaRecorder.start();

      const recordButton = this.shadowRoot.querySelector('[data-action="record"]');
      recordButton.classList.add('recording');
      recordButton.textContent = '🔴 Recording...';

      this.dispatchEvent(new CustomEvent('recording-start'));
    } catch (error) {
      this._handleError('Microphone access denied: ' + error.message);
      this._setState('idle');
    }
  }

  async _stopRecording() {
    if (this._state !== 'recording' || !this._mediaRecorder) return;

    this._setState('processing');

    const recordButton = this.shadowRoot.querySelector('[data-action="record"]');
    recordButton.classList.remove('recording');
    recordButton.textContent = '🎤 Hold to Talk';

    this._mediaRecorder.stop();

    this._mediaRecorder.onstop = async () => {
      // Create audio blob
      const audioBlob = new Blob(this._audioChunks, { type: 'audio/webm' });

      // Convert to base64
      const base64Audio = await this._blobToBase64(audioBlob);

      // Send to WebSocket
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(
          JSON.stringify({
            type: 'audio',
            data: base64Audio,
          })
        );
      }

      // Stop media stream
      if (this._mediaRecorder.stream) {
        this._mediaRecorder.stream.getTracks().forEach((track) => track.stop());
      }

      this.dispatchEvent(new CustomEvent('recording-stop'));
    };
  }

  _connectWebSocket() {
    if (this._ws && this._ws.readyState === WebSocket.OPEN) return;

    const wsUrl = this.getAttribute('ws-url') || this._getDefaultWsUrl();
    this._ws = new WebSocket(wsUrl);

    this._ws.onopen = () => {
      console.log('WebSocket connected');
      this._setStatus('connected', 'Connected');
      this._startPingInterval();
    };

    this._ws.onmessage = (event) => {
      this._handleWebSocketMessage(event);
    };

    this._ws.onerror = (error) => {
      console.error('WebSocket error:', error);
      this._handleError('Connection error');
      this._setStatus('disconnected', 'Disconnected');
    };

    this._ws.onclose = () => {
      console.log('WebSocket closed');
      this._setStatus('disconnected', 'Disconnected');
      this._stopPingInterval();
    };
  }

  _handleWebSocketMessage(event) {
    const message = JSON.parse(event.data);

    switch (message.type) {
      case 'transcription':
        this._displayTranscription(message.text);
        this.dispatchEvent(new CustomEvent('transcription', { detail: message }));
        break;

      case 'response':
        this._displayResponse(message.text, message.tokens, message.thinking);
        this.dispatchEvent(new CustomEvent('response', { detail: message }));
        this._setState('idle');
        break;

      case 'error':
        this._handleError(message.message);
        this._setState('idle');
        break;

      case 'pong':
        // Keepalive response - no action needed
        break;

      default:
        console.warn('Unknown message type:', message.type);
    }
  }

  _displayTranscription(text) {
    this._addMessage('user', text);
  }

  _displayResponse(text, tokens, thinking) {
    // Display thinking if available (as system message)
    if (thinking) {
      this._addMessage('system', `💭 Claude is thinking: ${thinking.substring(0, 100)}...`);
    }

    // Display response
    this._addMessage('assistant', text);

    // Update token display
    this._updateTokenDisplay(tokens);
  }

  _addMessage(role, text) {
    const messagesContainer = this.shadowRoot.querySelector('[data-display="messages"]');

    // Create message element
    const messageEl = document.createElement('div');
    messageEl.className = `message ${role}`;

    // Create content
    const contentEl = document.createElement('div');
    contentEl.textContent = text;

    // Create timestamp
    const timestampEl = document.createElement('div');
    timestampEl.className = 'timestamp';
    timestampEl.textContent = new Date().toLocaleTimeString();

    messageEl.appendChild(contentEl);
    messageEl.appendChild(timestampEl);
    messagesContainer.appendChild(messageEl);

    // Auto-scroll to bottom
    messagesContainer.scrollTop = messagesContainer.scrollHeight;
  }

  _updateTokenDisplay(tokens) {
    // Update session totals
    this.sessionTokens.push(tokens);
    this.totalTokensUsed += tokens.total;

    // Set component attributes for external access
    this.setAttribute('data-input-tokens', tokens.input);
    this.setAttribute('data-output-tokens', tokens.output);
    this.setAttribute('data-total-tokens', tokens.total);
    this.setAttribute('data-session-total-tokens', this.totalTokensUsed);
    this.setAttribute('data-request-count', this.sessionTokens.length);
  }

  _handleError(message) {
    this._addMessage('system', `⚠️ Error: ${message}`);
    this._setStatus('disconnected', 'Error');
    this.dispatchEvent(new CustomEvent('error', { detail: { message } }));
  }

  _setStatus(statusClass, statusText) {
    const statusEl = this.shadowRoot.querySelector('[data-display="status"]');
    statusEl.className = `status ${statusClass}`;
    statusEl.textContent = statusText;
  }

  _setState(state) {
    this._state = state;

    const recordButton = this.shadowRoot.querySelector('[data-action="record"]');

    switch (state) {
      case 'idle':
        recordButton.disabled = false;
        this._setStatus('connected', 'Connected');
        break;

      case 'recording':
        recordButton.disabled = false;
        this._setStatus('recording', '🎤 Recording...');
        break;

      case 'processing':
        recordButton.disabled = true;
        this._setStatus('connected', 'Processing...');
        break;
    }
  }

  _startPingInterval() {
    this._stopPingInterval();

    this._pingInterval = setInterval(() => {
      if (this._ws && this._ws.readyState === WebSocket.OPEN) {
        this._ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Ping every 30 seconds
  }

  _stopPingInterval() {
    if (this._pingInterval) {
      clearInterval(this._pingInterval);
      this._pingInterval = null;
    }
  }

  async _blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        // Remove data URL prefix (data:audio/webm;base64,)
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  _getDefaultWsUrl() {
    // Construct WebSocket URL from current location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.host;
    return `${protocol}//${host}/ws/assistant`;
  }

  _cleanup() {
    // Close WebSocket
    if (this._ws) {
      this._ws.close();
      this._ws = null;
    }

    // Stop recording
    if (this._mediaRecorder && this._mediaRecorder.state === 'recording') {
      this._mediaRecorder.stop();
    }

    // Stop ping interval
    this._stopPingInterval();
  }
}

